import { createAsyncThunk } from "@reduxjs/toolkit";
import { quizzesApi, paywallsApi, projectsApi } from "@/api";
import { extractApiError } from "@/api/errorStatus";
import type { Project } from "@/types";
import { setCurrentProject, switchActiveProjectThunk } from "../../projectsSlice";
import { isOpenResource } from "../openResource";
import type { EditorState } from "../state";
import { rememberCustomStyleOf } from "@/themes/customStylePack";

// WEB-2552 (пачка 49, заход Р1): санки загрузки квиза/пейволла вынесены из
// editorSlice.ts («перенос токен в токен») вместе с их приватными
// помощниками (isNotFound/findResourceAcrossProjects/persistProjectSwitch) —
// они нужны только этим двум санкам. Строковые имена санков
// (`editor/loadQuiz`, `editor/loadPaywall`) не менялись. extraReducers у
// этих санков остались в фасаде — там же, где разбор ответа (parseContent) и
// умолчание языка воронки (DEFAULT_FUNNEL_LANGUAGE, сторож WEB-1909 читает
// оба дома).

function isNotFound(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /not found/i.test(error.message);
}

/**
 * WEB-3399: копия своего стиля открытой части — в реестр копий до возврата
 * ответа (настройки — `draftSettings ?? settings`, как кладёт редьюсер); в
 * редьюсере побочного действия нет. Ответ отдаётся как есть.
 */
function withCustomStyleRemembered<T extends { data: Record<string, unknown> }>(
  loaded: T,
): T {
  rememberCustomStyleOf(loaded.data.draftSettings ?? loaded.data.settings);
  return loaded;
}

// WEB-336: orgId обязателен чтобы не получить 400 «Multiple organizations»
async function findResourceAcrossProjects<T>(
  attempt: (projectId: string) => Promise<T>,
  excludeProjectId: string,
  orgId?: string,
): Promise<{ projectId: string; data: T } | null> {
  const listResponse = await projectsApi.list(orgId);
  const projects = (listResponse.data as { projects: Project[] }).projects;
  for (const project of projects) {
    if (project.id === excludeProjectId) continue;
    try {
      const data = await attempt(project.id);
      return { projectId: project.id, data };
    } catch {
      /* try next project */
    }
  }
  return null;
}

/**
 * WEB-1368 п.1.1 — редактор, нашедший воронку в другом проекте, раньше
 * переставлял активный проект ТОЛЬКО локально (setCurrentProject): выбор не
 * доезжал до сервера, и после перезагрузки человека возвращало в прежний
 * проект без объяснений. Теперь сначала зовётся серверный путь смены проекта
 * (switchActiveProjectThunk → PATCH /user/active-project), и лишь затем проект
 * ставится в хранилище.
 *
 * Порядок важен: switchActiveProjectThunk.fulfilled ищет проект в state.projects,
 * а в редакторе список мог не грузиться — поэтому итоговое значение ставит
 * setCurrentProject, идущий ПОСЛЕ. Отказ сервера не запирает редактор: проект
 * всё равно применяется локально (прежнее поведение как запасной путь).
 */
async function persistProjectSwitch(
  dispatch: (action: unknown) => unknown,
  project: Project,
): Promise<void> {
  try {
    await (dispatch(switchActiveProjectThunk(project.id)) as unknown as Promise<unknown>);
  } catch {
    /* сеть упала — остаётся локальное применение ниже */
  }
  dispatch(setCurrentProject(project));
}

/**
 * WEB-2931: перечитывание ресурса, который уже открыт (после отката версии),
 * — не открытие. Защита от опоздавших ответов (`isCurrentLoad`) принимает
 * ответ ПОСЛЕДНЕГО запущенного открытия, и перечитывание А, запущенное после
 * того, как автор ушёл на Б, само становилось последним: номер запроса Б
 * перебивался, ответ Б отбрасывался, в редакторе под адресом Б оказывался А.
 * С признаком `onlyIfOpen` санк не стартует вовсе (ни запроса, ни `pending`),
 * если в редакторе открыт не этот ресурс (другой, или редактор сброшен уходом
 * со страницы) или идёт открытие (переход на другой ресурс ещё не закончился
 * — `resourceId` в это время ещё прежний).
 * Граница: если в момент проверки идёт перечитывание ЭТОГО же ресурса другим
 * путём, перечитывание тоже пропускается — номер запроса не говорит, какой
 * ресурс грузится.
 */
export interface ReloadIfOpen {
  onlyIfOpen?: boolean;
}

function stillOpen(
  getState: () => unknown,
  kind: "quiz" | "paywall",
  id: string,
): boolean {
  const editor = (getState() as { editor?: EditorState }).editor;
  return !!editor && isOpenResource(editor, kind, id) && !editor.isLoading;
}

export const loadQuizForEditor = createAsyncThunk(
  "editor/loadQuiz",
  async (
    { projectId, quizId }: { projectId: string; quizId: string } & ReloadIfOpen,
    { rejectWithValue, dispatch, getState },
  ) => {
    // WEB-336: берём activeOrgId из стора чтобы scoped-запросы не давали 400
    const activeOrgId =
      (getState() as { organizations?: { activeOrgId: string | null } })
        .organizations?.activeOrgId ?? undefined;
    try {
      const response = await quizzesApi.get(projectId, quizId);
      return withCustomStyleRemembered({
        projectId,
        data: response.data as Record<string, unknown>,
      });
    } catch (error: unknown) {
      if (isNotFound(error)) {
        const fallback = await findResourceAcrossProjects(
          async (pid) =>
            (await quizzesApi.get(pid, quizId)).data as Record<string, unknown>,
          projectId,
          activeOrgId,
        );
        if (fallback) {
          const listResponse = await projectsApi.list(activeOrgId);
          const projects = (listResponse.data as { projects: Project[] })
            .projects;
          const project = projects.find((p) => p.id === fallback.projectId);
          if (project) await persistProjectSwitch(dispatch, project);
          return withCustomStyleRemembered({
            projectId: fallback.projectId,
            data: fallback.data,
          });
        }
      }
      // WEB-1955: отказ отвергается РАЗОБРАННЫМ — иначе код (например
      // RESOURCE_READONLY, NOT_FOUND) умирает в слайсе, и экран показывает
      // общую фразу вместо причины.
      return rejectWithValue(extractApiError(error));
    }
  },
  {
    condition: ({ quizId, onlyIfOpen }, { getState }) =>
      !onlyIfOpen || stillOpen(getState, "quiz", quizId),
  },
);

export const loadPaywallForEditor = createAsyncThunk(
  "editor/loadPaywall",
  async (
    { projectId, paywallId }: { projectId: string; paywallId: string } & ReloadIfOpen,
    { rejectWithValue, dispatch, getState },
  ) => {
    // WEB-336: берём activeOrgId из стора чтобы scoped-запросы не давали 400
    const activeOrgId =
      (getState() as { organizations?: { activeOrgId: string | null } })
        .organizations?.activeOrgId ?? undefined;
    try {
      const response = await paywallsApi.get(projectId, paywallId);
      return withCustomStyleRemembered({
        projectId,
        data: response.data as Record<string, unknown>,
      });
    } catch (error: unknown) {
      if (isNotFound(error)) {
        const fallback = await findResourceAcrossProjects(
          async (pid) =>
            (await paywallsApi.get(pid, paywallId)).data as Record<
              string,
              unknown
            >,
          projectId,
          activeOrgId,
        );
        if (fallback) {
          const listResponse = await projectsApi.list(activeOrgId);
          const projects = (listResponse.data as { projects: Project[] })
            .projects;
          const project = projects.find((p) => p.id === fallback.projectId);
          if (project) await persistProjectSwitch(dispatch, project);
          return withCustomStyleRemembered({
            projectId: fallback.projectId,
            data: fallback.data,
          });
        }
      }
      return rejectWithValue(extractApiError(error));
    }
  },
  {
    condition: ({ paywallId, onlyIfOpen }, { getState }) =>
      !onlyIfOpen || stillOpen(getState, "paywall", paywallId),
  },
);
